import React from 'react';
import { useTranslation } from 'react-i18next';
import Flex from 'ui/Flex/Flex';
import { styled } from '@mui/material/styles';
import { alpha, Button, Stack, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

type Props = {
    type: 'metrics' | 'dimensions' | 'filters';
    children: React.ReactNode[];
    addElement: () => void;
    isButtonDisabled?: boolean;
    disabledButtonTooltip?: string;
};

export const SENTENCE_FONT_SIZE = '14px';
// if the font-size of Sentence changes, this value should be updated to ensure alignment between buttons and inputs
export const SENTENCE_FIRST_LABELS_MARGIN = '6em';

const StySectionContainer = styled(Flex, {
    name: 'StySectionContainer',
})(({ theme }) => ({
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '1rem',
    paddingLeft: '0.75rem',
    borderRadius: '5px',
    [theme.breakpoints.up('lg')]: {
        flexDirection: 'row',
        alignItems: 'initial',
        paddingLeft: '1rem',
    },
    [theme.breakpoints.up('xl')]: {
        paddingLeft: '3rem',
    },
}));

const StyAddButton = styled(Button, {
    name: 'StyAddButton',
})(({ theme }) => ({
    minHeight: '1.75rem',
    fontSize: SENTENCE_FONT_SIZE,
    backgroundColor: theme.palette.primary.background,
    // increasing specificity because this component is wrapped by a Stack, which set margins
    '&.MuiButton-root': {
        [theme.breakpoints.up('md')]: {
            marginLeft: `calc(3em + 1rem)`,
        },
        [theme.breakpoints.up('lg')]: {
            marginLeft: `calc(${SENTENCE_FIRST_LABELS_MARGIN} + 1rem)`,
        },
    },
}));

const StySectionTitle = styled(Typography, {
    name: 'StySectionTitle',
})(({ theme }) => ({
    zIndex: 1,
    color: theme.palette.primary.main,
    marginRight: 0,
    lineHeight: 2,
    fontWeight: 600,
    fontSize: SENTENCE_FONT_SIZE,
    width: '8em',
    flexShrink: 0,
    whiteSpace: 'nowrap', // we don't want the colon to be on a new line
    [theme.breakpoints.up('lg')]: {
        marginRight: '0.5rem',
        alignSelf: 'center',
    },
    [theme.breakpoints.up('xl')]: {
        marginRight: '3rem',
    },
}));

const SentenceSection = (props: Props): React.ReactElement => {
    const {
        isButtonDisabled = false,
        type,
        children,
        addElement,
        disabledButtonTooltip,
    } = props;
    const { t } = useTranslation();
    const title = t(`sentence.sectionTitle.${type}`);
    const buttonTitle = t(`sentence.add.${type}`);
    const buttonComponent = (
        <StyAddButton
            variant="outlined"
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            disabled={isButtonDisabled}
            onClick={addElement}
        >
            <Typography
                sx={{ color: 'inherit', fontWeight: 400 }}
                variant="body1SemiBold"
            >
                {buttonTitle}
            </Typography>
        </StyAddButton>
    );
    const buttonWithTooltip = (
        <Tooltip title={disabledButtonTooltip}>
            <span>{buttonComponent}</span>
        </Tooltip>
    );
    return (
        <StySectionContainer>
            <StySectionTitle>{`${title} :`}</StySectionTitle>
            <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
                {children}
                {isButtonDisabled && disabledButtonTooltip
                    ? buttonWithTooltip
                    : buttonComponent}
            </Stack>
        </StySectionContainer>
    );
};

export default SentenceSection;
